import { t } from 'i18next';
import { MutateOptions, useMutation, useQuery } from 'react-query';

import { IImportPayload } from 'bom/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useQueryChecker } from 'core/hooks';
import { omitVendorMappingFromImportPayload } from 'quotes/mappers';
import { importService } from 'quotes/services';
import {
  IExtractDataProgress,
  IImportStatus,
  IImportVendorQuotePayload,
  IVendorQuote,
} from 'quotes/types';

type IProps = {
  position?: NotificationPosition;
};

export const useImportQuoteActions = (props: IProps = {}) => ({
  ...useCreateVendorQuoteImport(props),
  ...useImportQuoteToQuote(props),
  ...useImportQuoteToProject(props),
  ...useImportQuoteToJob(props),
  ...useCreateQuoteImport(props),
});

// The three finalize mutations, so a caller can hand over a set bound to another notification
// position (the import wizard reports its finalize inside its own drawer).
export type TImportQuoteActions = Pick<
  ReturnType<typeof useImportQuoteActions>,
  'importQuoteToJob' | 'importQuoteToProject' | 'importQuoteToQuote'
>;

export const useValidateVendorQuoteBeforeImport = (
  criteria: IImportVendorQuotePayload,
  enabled = false,
  onSuccess: ({ record }: { record: IVendorQuote }) => void
) => {
  const { data, isLoading, isError, error, refetch } = useQuery(
    // The vendor bag never reaches this endpoint, so it must not key its cache either: a re-picked
    // vendor would otherwise re-key the still-mounted query and phantom-fire a second validate.
    ['validateVendorQuoteBeforeImport', omitVendorMappingFromImportPayload(criteria)],
    ({ signal }) => importService.validateVendorQuoteBeforeImport(criteria, signal),
    {
      enabled,
      onSuccess,
    }
  );

  return {
    validatedVendorQuote: data?.record ?? null,
    isLoading,
    isError,
    error,
    refetch,
  };
};

// Imperative twin of the query hook above: the gated import wizard must await the dedup result and
// be able to re-run it for a re-picked contact. A query can't do that — react-query replays its
// cache for an unchanged key, so `onSuccess` would not fire again and the check would be skipped.
export const useValidateVendorQuoteBeforeImportAction = ({
  position = NotificationPosition.DRAWER,
}: IProps = {}) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleVendorQuoteImport') },
    position,
  });

  const { mutateAsync } = useMutation(
    (requestPayload: IImportVendorQuotePayload) =>
      importService.validateVendorQuoteBeforeImport(requestPayload),
    // onError only: the service resolves with the mapRecordFromServer shape, whose `success` key is
    // present but undefined, which the onSuccess handler misreads as a failed response and toasts
    // "Server error." on every check.
    { onError: errors.onError }
  );

  return { validateVendorQuoteBeforeImport: mutateAsync };
};

export const useImportExtractDataChecker = ({
  criteria,
  enabled = false,
  onSuccess,
  position = NotificationPosition.DRAWER,
}: {
  criteria: IImportPayload;
  enabled?: boolean;
  onSuccess: (data: IExtractDataProgress) => void;
  position?: NotificationPosition;
}) => {
  useQueryChecker<IExtractDataProgress>({
    fetchData: importService.extractDataProgress,
    queryKey: 'importExtractData',
    requestPayload: criteria,
    enabled,
    isFetchDataFinished: (data: IExtractDataProgress) => data?.finished,
    onSuccess,
    options: {
      loading: {
        title: t('titleStatus'),
        message: t('messageDataExtractingInProgress'),
      },
      error: {
        title: t('errorTitleDataExtracting'),
        message: t('serverError'),
      },
      notificationPosition: position,
    },
  });
};

export const useImportQuoteChecker = ({
  criteria,
  enabled = false,
  onSuccess,
  position = NotificationPosition.DRAWER,
}: {
  criteria: IImportPayload;
  enabled?: boolean;
  onSuccess: () => void;
  position?: NotificationPosition;
}) =>
  useQueryChecker<IImportStatus>({
    fetchData: importService.importStatus,
    queryKey: 'importStatus',
    requestPayload: criteria,
    enabled,
    isFetchDataFinished: (data: IImportStatus) => data?.finished,
    isFetchDataFailed: (data: IImportStatus) => !data?.success,
    onSuccess,
    options: {
      success: {
        title: t('titleStatus'),
        message: t('messageDataImportSuccess'),
      },
      loading: {
        title: t('titleStatus'),
        message: t('messageDataImportingInProgress'),
      },
      error: {
        title: t('errorTitleDataImporting'),
        message: t('serverError'),
      },
      notificationPosition: position,
    },
  });

const useCreateVendorQuoteImport = ({ position = NotificationPosition.DRAWER }: IProps) => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageVendorQuoteImportCreatingInProgress') },
    error: { title: t('errorTitleVendorQuoteImportCreating') },
    position,
  });

  const { mutateAsync } = useMutation(importService.createVendorQuoteImport, {
    ...errors,
  });

  const createVendorQuoteImport = (data) => mutateAsync(data);

  return { createVendorQuoteImport };
};

const useImportQuoteToQuote = ({ position = NotificationPosition.FORM }: IProps) => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageVendorQuoteImportStartInProgress') },
    error: { title: t('errorTitleVendorQuoteImport') },
    position,
  });

  const { mutateAsync } = useMutation(importService.importQuoteToQuote, {
    ...errors,
  });

  const importQuoteToQuote = (data, options: MutateOptions) => mutateAsync(data, options as any);

  return { importQuoteToQuote };
};

const useImportQuoteToProject = ({ position = NotificationPosition.FORM }: IProps) => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageVendorQuoteImportStartInProgress') },
    error: { title: t('errorTitleVendorQuoteImport') },
    position,
  });

  const { mutateAsync } = useMutation(importService.importQuoteToProject, {
    ...errors,
  });

  const importQuoteToProject = (data, options: MutateOptions) => mutateAsync(data, options as any);

  return { importQuoteToProject };
};

const useImportQuoteToJob = ({ position = NotificationPosition.FORM }: IProps) => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageVendorQuoteImportStartInProgress') },
    error: { title: t('errorTitleVendorQuoteImport') },
    position,
  });

  const { mutateAsync } = useMutation(importService.importQuoteToJob, {
    ...errors,
  });

  const importQuoteToJob = (data, options?: MutateOptions) => mutateAsync(data, options as any);

  return { importQuoteToJob };
};

const useCreateQuoteImport = ({ position = NotificationPosition.DRAWER }: IProps) => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageQuoteImportCreateInProgress') },
    error: { title: t('errorTitleQuoteImportCreating') },
    position,
  });

  const { mutateAsync } = useMutation(importService.createQuoteImport, {
    ...errors,
  });

  return { createQuoteImport: mutateAsync };
};
